// frontend/src/components/checklists/ChecklistsByReviewIdDevTool.tsx
import React from "react";
import { Badge, Box, Code, Group, Loader, Stack, Text, TextInput, Title } from "@mantine/core";
import { useAppSelector } from "../hooks";
import { FailedAlert } from "../components";
import { useGetChecklistsByReviewByReviewIdQuery } from "../../openapi";

const DEFAULT_REVIEW_ID = "15ec1374-fba2-48cf-a4d6-f93e682949dd";

const isValidReviewId = (v: string) => v.trim().length >= 1;

export const ChecklistsByReviewId: React.FC = () => {
  const [reviewId, setReviewId] = React.useState<string>(DEFAULT_REVIEW_ID);
  const xApiKey = useAppSelector((state) => state.globalReducer.xApiKey);

  const valid = isValidReviewId(reviewId) && xApiKey != null;

  const { data, isLoading, isFetching, isSuccess, isError, error, startedTimeStamp, fulfilledTimeStamp } =
    useGetChecklistsByReviewByReviewIdQuery(
      { reviewId: reviewId.trim() },
      {
        skip: !valid,
      },
    );

  const busy = isLoading || isFetching;

  const requestTime =
    isSuccess && startedTimeStamp && fulfilledTimeStamp ? fulfilledTimeStamp - startedTimeStamp : null;

  const lastRequestTimestamp = fulfilledTimeStamp ? new Date(fulfilledTimeStamp).toLocaleString("no-NO") : null;

  return (
    <Box>
      {isError && <FailedAlert />}

      <Stack>
        <TextInput
          label="reviewId"
          value={reviewId}
          onChange={(e) => setReviewId(e.currentTarget.value)}
          placeholder={DEFAULT_REVIEW_ID}
          description="Lim inn en Doksam reviewId. Kallet trigges automatisk når feltet ikke er tomt."
          error={reviewId.length > 0 && !isValidReviewId(reviewId) ? "Ugyldig reviewId" : undefined}
        />

        {!valid && (
          <Badge color="gray" leftSection={<i className="fa fa-info-circle" />}>
            Skip: venter på x-api-key og reviewId
          </Badge>
        )}

        {busy ? (
          <Loader size="sm" />
        ) : (
          <>
            {isSuccess && valid && (
              <Stack gap="xs">
                <Badge color="green" leftSection={<i className="fa fa-check" />}>
                  Vellykket anrop ({Array.isArray(data) ? data.length : 0} checklister)
                </Badge>

                {requestTime !== null && (
                  <Text size="sm" c="dimmed">
                    Anropstid: {requestTime.toFixed(2)} ms
                  </Text>
                )}

                {lastRequestTimestamp && (
                  <Text size="sm" c="dimmed">
                    Sist kjørt: {lastRequestTimestamp}
                  </Text>
                )}
              </Stack>
            )}

            {isError && valid && (
              <Stack gap="xs">
                <Badge color="red" leftSection={<i className="fa fa-times" />}>
                  Feil ved anrop
                </Badge>
                <Text size="sm" c="dimmed">
                  {typeof error === "object" ? JSON.stringify(error) : String(error)}
                </Text>
              </Stack>
            )}

            <Stack gap={6} mt="xs">
              <Text fw={500}>Respons (JSON)</Text>
              <Box
                p="sm"
                style={{
                  border: "1px solid var(--mantine-color-gray-3)",
                  borderRadius: 8,
                  overflow: "auto",
                  maxHeight: 420,
                }}
              >
                <Code block>{JSON.stringify(data ?? null, null, 2)}</Code>
              </Box>
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
};

export default ChecklistsByReviewId;
